import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type Manifest } from '@/api/client';
import { useDocumentTitle } from '@/compliance/ResearchUse';
import { InventoryPanel } from '@/inventory/InventoryPanel';
import { MprPanel, MprPane } from '@/mpr/MprPanel';
import type { PaneId } from '@/mpr/MprSet';
import { Compass } from './Compass';
import { ControlsGuideDialog, ViewSettingsDialog, type ViewSpeeds } from './dialogs';
import { PanelResizer } from './PanelResizer';
import { TheaterPanel } from './TheaterPanel';
import { TheaterSession } from './TheaterSession';
import { useSessionState } from './useSession';
import { Viewer } from './Viewer';
import './theater.css';
import { Icon } from '@/ui/Icon';

type Layout = { mode: 'normal' } | { mode: 'quad' } | { mode: 'fullscreen'; pane: PaneId; from: 'normal' | 'quad' };

const SPEEDS_KEY = 'valvia.viewSpeeds';
const WIDTHS_KEY = 'valvia.panelWidths';
/** Under this width the sidebar panel overlays the stage (theater.css), so it starts closed. */
const NARROW = '(max-width: 900px)';
/** Dragged panel widths (px); null keeps the stylesheet default. theater.css also caps each at
 * 35vw so the stage keeps room when the window shrinks; the drag clamp matches. */
type PanelWidths = { sidebar: number | null; mpr: number | null };
const panelMax = (hard: number) => () => Math.min(hard, window.innerWidth * 0.35);

function loadWidths(): PanelWidths {
  try {
    const raw = localStorage.getItem(WIDTHS_KEY);
    if (raw) return { sidebar: null, mpr: null, ...(JSON.parse(raw) as Partial<PanelWidths>) };
  } catch {
    /* ignore */
  }
  return { sidebar: null, mpr: null };
}

function defaultSpeeds(): ViewSpeeds {
  return { rotate3d: 1, pan3d: 1, zoom3d: 1, rotate2d: 1, pan2d: 1, zoom2d: 1 };
}
function loadSpeeds(): ViewSpeeds {
  try {
    const raw = localStorage.getItem(SPEEDS_KEY);
    if (raw) return { ...defaultSpeeds(), ...(JSON.parse(raw) as Partial<ViewSpeeds>) };
  } catch {
    /* ignore */
  }
  return defaultSpeeds();
}

export default function TheaterPage() {
  const { runId = '' } = useParams();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<TheaterSession | null>(null);
  const [tab, setTab] = useState<'inventory' | 'theater'>('inventory');
  const [layout, setLayout] = useState<Layout>({ mode: 'normal' });
  const [sidebarOpen, setSidebarOpen] = useState(() => !window.matchMedia(NARROW).matches);
  const [mprOpen, setMprOpen] = useState(true);
  const [guide, setGuide] = useState(false);
  const [settings, setSettings] = useState(false);
  const [speeds, setSpeeds] = useState<ViewSpeeds>(loadSpeeds);
  const [widths, setWidths] = useState<PanelWidths>(loadWidths);
  const containerRef = useRef<HTMLDivElement>(null);
  const sidebarBodyRef = useRef<HTMLDivElement>(null);
  const mprRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const state = useSessionState(session);
  useDocumentTitle(manifest ? `Valvia - ${manifest.project_name}` : 'Valvia');

  useEffect(() => {
    api
      .get<Manifest>(`/api/runs/${runId}/manifest`)
      .then(setManifest)
      .catch((e) => setError((e as Error).message));
  }, [runId]);

  useEffect(() => {
    if (!manifest || !containerRef.current || !mainRef.current || !labelsRef.current) return;
    const viewer = new Viewer(containerRef.current, mainRef.current, labelsRef.current);
    viewerRef.current = viewer;
    const s = new TheaterSession(viewer, manifest);
    setSession(s);
    void s.load();
    return () => {
      s.dispose();
      viewer.dispose();
      viewerRef.current = null;
      setSession(null);
    };
  }, [manifest]);

  useEffect(() => {
    const v = viewerRef.current;
    if (!v) return;
    v.controls.speeds = { rotate: speeds.rotate3d, pan: speeds.pan3d, zoom: speeds.zoom3d };
    try {
      localStorage.setItem(SPEEDS_KEY, JSON.stringify(speeds));
    } catch {
      /* ignore */
    }
  }, [speeds, session]);

  // The views draw into their elements' rects, so a panel drag only needs a redraw.
  useEffect(() => {
    viewerRef.current?.invalidate();
    try {
      localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths));
    } catch {
      /* ignore */
    }
  }, [widths]);

  // Keyboard: F focus, A/P/L/R/S/I snaps (never while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (!session || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT') return;
      const k = e.key.toUpperCase();
      if (k === 'F') session.focusVolume();
      else if (['A', 'P', 'L', 'R', 'S', 'I'].includes(k)) session.snapToLabel(k);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session]);

  // Re-layout after the panels change (the canvas rects follow the DOM).
  useEffect(() => {
    const id = window.setTimeout(() => viewerRef.current?.invalidate(), 50);
    return () => window.clearTimeout(id);
  }, [layout, sidebarOpen, mprOpen, tab]);

  const toggleFullscreen = useCallback((pane: PaneId) => {
    setLayout((l) => {
      if (l.mode === 'fullscreen' && l.pane === pane) return l.from === 'quad' ? { mode: 'quad' } : { mode: 'normal' };
      return { mode: 'fullscreen', pane, from: l.mode === 'quad' ? 'quad' : 'normal' };
    });
  }, []);

  if (error) return <div className="page error">{error}</div>;
  if (!manifest) return <div className="page muted">Loading…</div>;

  const quad = layout.mode === 'quad';
  const fullscreen = layout.mode === 'fullscreen' ? layout.pane : null;
  const frameCount = Math.max(1, manifest.num_frames);
  const mainClass = fullscreen ? 'main-view hidden-view' : quad ? 'main-view quad-top-right' : 'main-view';
  const showMpr = !quad && !fullscreen && session;
  const widthVars = {
    '--sidebar-w': widths.sidebar ? `${widths.sidebar}px` : undefined,
    '--mpr-w': widths.mpr ? `${widths.mpr}px` : undefined,
  } as CSSProperties;

  return (
    <div className={`theater ${sidebarOpen ? '' : 'sidebar-collapsed'}`} ref={containerRef} style={widthVars}>
      <div className="theater-sidebar">
        <div className="sidebar-rail">
          <Link to={`/projects/${manifest.project_id}`} className="rail-btn" title="Go back to Gallery">
            <Icon name="chevron-left" />
          </Link>
          <button className={`rail-btn ${tab === 'inventory' && sidebarOpen ? 'on' : ''}`} title="Inventory (measurements)" onClick={() => (tab === 'inventory' ? setSidebarOpen(!sidebarOpen) : (setTab('inventory'), setSidebarOpen(true)))}>
            <Icon name="heart" />
          </button>
          <button className={`rail-btn ${tab === 'theater' && sidebarOpen ? 'on' : ''}`} title="Theater (volume rendering & presets)" onClick={() => (tab === 'theater' ? setSidebarOpen(!sidebarOpen) : (setTab('theater'), setSidebarOpen(true)))}>
            <Icon name="box" />
          </button>
          <span className="spacer" />
          <button className="rail-btn" title="View settings" onClick={() => setSettings(true)}>
            <Icon name="settings" />
          </button>
          <button className="rail-btn" title="Controls guide" onClick={() => setGuide(true)}>
            ?
          </button>
        </div>
        {sidebarOpen && session && state && (
          <div className="sidebar-body" ref={sidebarBodyRef}>
            {tab === 'inventory' ? <InventoryPanel session={session} state={state} /> : <TheaterPanel session={session} state={state} />}
          </div>
        )}
      </div>
      {sidebarOpen && session && state && (
        <PanelResizer panel={sidebarBodyRef} edge="right" min={220} max={panelMax(640)} onResize={(w) => setWidths((s) => ({ ...s, sidebar: w }))} onReset={() => setWidths((s) => ({ ...s, sidebar: null }))} />
      )}

      <div className="theater-center">
        {/* The 3D view element never remounts: the Viewer and its controls hold it. */}
        <div className={mainClass} ref={mainRef}>
          <div className="labels-layer" ref={labelsRef} />
          {session && state && <Compass session={session} state={state} />}
          <div className="quick-bar">
            {state && session && (
              <>
                <button className={state.showLabels ? 'on' : ''} title="Show label" onClick={() => session.setShowLabels(!state.showLabels)}>
                  Labels
                </button>
                <button className={state.hideAll ? 'on' : ''} title="Hide all inventory & labels on every frame (override — each row keeps its own eye state)" onClick={() => session.setHideAll(!state.hideAll)}>
                  Hide all
                </button>
                <button className={state.showVolume ? 'on' : ''} title="Show volumetric" onClick={() => session.setShowVolume(!state.showVolume)}>
                  Volume
                </button>
                <button className={state.orthographic ? 'on' : ''} title="Orthographic camera" onClick={() => session.setOrthographic(!state.orthographic)}>
                  Ortho
                </button>
                <button title="Toggle Clean Root / Endocast preset" onClick={() => session.applyPreset(state.presetName === 'Clean Root' ? 'Endocast' : 'Clean Root')}>
                  {state.presetName === 'Clean Root' ? 'Endocast' : 'Clean Root'}
                </button>
                <button className={quad ? 'on' : ''} title="Toggle the 2 × 2 quad view" onClick={() => setLayout(quad ? { mode: 'normal' } : { mode: 'quad' })}>
                  Quad
                </button>
                {state.corrections && (
                  <select value={state.corrections.snapMode} title="Where a dragged point snaps: the first visible voxel, the segmentation surfaces, whichever is nearer, or nothing (Ctrl while dragging also disables snapping)" onChange={(e) => session.setSnapMode(e.target.value as 'volumetric' | 'surface' | 'both' | 'off')}>
                    <option value="both">Snap: both</option>
                    <option value="volumetric">Snap: volume</option>
                    <option value="surface">Snap: surface</option>
                    <option value="off">Snap: off</option>
                  </select>
                )}
                {frameCount > 1 && (
                  <>
                    <button title="Previous frame" onClick={() => session.prevFrame()}>
                      <Icon name="skip-back" />
                    </button>
                    <button title={state.playing ? 'Pause playback' : 'Play frames'} onClick={() => (state.playing ? session.pause() : session.play())}>
                      <Icon name={state.playing ? 'pause' : 'play'} />
                    </button>
                    <button title="Next frame" onClick={() => session.nextFrame()}>
                      <Icon name="skip-forward" />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          {frameCount > 1 && state && session && (
            <div className="frame-bar">
              {manifest.frames.map((f) => {
                const loaded = state.loadedFrames.has(f.index);
                const pending = state.pendingFrame === f.index;
                const cls = [
                  state.frame === f.index && state.pendingFrame === null ? 'selected' : '',
                  pending ? 'pending' : '',
                  !loaded && !pending ? 'unloaded' : '',
                  f.is_primary ? 'primary' : '',
                ].join(' ');
                return (
                  <button
                    key={f.index}
                    className={cls}
                    title={`${f.label ?? ''}${f.label ? ' R-R · ' : ''}Frame ${f.index + 1} / ${frameCount}${f.is_primary ? ' · pipeline target' : ''}${
                      pending ? ' · downloading' : loaded ? '' : ' · not loaded yet, click to download'
                    }`}
                    onClick={() => void session.setFrame(f.index)}
                  >
                    {f.label && f.label.endsWith('%') ? f.label : f.index + 1}
                  </button>
                );
              })}
              {state.pendingFrame !== null && (
                <span className="frame-status">
                  <span className="frame-spinner" />
                  Loading frame {state.pendingFrame + 1}…
                </span>
              )}
              {state.pendingFrame === null && state.frameError && <span className="frame-status error">{state.frameError}</span>}
            </div>
          )}
        </div>
        {quad && !fullscreen && session && (
          <>
            <MprPane session={session} pane="y" className="cell quad-top-left" onFullscreen={() => toggleFullscreen('y')} />
            <MprPane session={session} pane="x" className="cell quad-bottom-left" onFullscreen={() => toggleFullscreen('x')} />
            <MprPane session={session} pane="z" className="cell quad-bottom-right" onFullscreen={() => toggleFullscreen('z')} />
            <button className="quad-exit" title="Exit four-panel view" onClick={() => setLayout({ mode: 'normal' })}>
              <Icon name="x" />
            </button>
          </>
        )}
        {fullscreen && session && <MprPane session={session} pane={fullscreen} className="cell cell-full" onFullscreen={() => toggleFullscreen(fullscreen)} fullscreen />}
        {state?.loading && (
          <div className="loading-overlay">
            <div>{state.loading}</div>
            <div className="muted small">{(state.bytesLoaded / 1024 / 1024).toFixed(1)} MB of ~{(session!.totalAssetBytes() / 1024 / 1024).toFixed(1)} MB</div>
          </div>
        )}
        {state?.error && <div className="loading-overlay error">{state.error}</div>}
      </div>

      {showMpr && mprOpen && (
        <PanelResizer panel={mprRef} edge="left" min={240} max={panelMax(900)} onResize={(w) => setWidths((s) => ({ ...s, mpr: w }))} onReset={() => setWidths((s) => ({ ...s, mpr: null }))} />
      )}
      {showMpr && (
        <div className={`theater-mpr ${mprOpen ? '' : 'collapsed'}`} ref={mprRef} onScroll={() => viewerRef.current?.invalidate()}>
          <button className="mpr-collapse" title={mprOpen ? 'Collapse' : 'Multiplanar Reformatting'} onClick={() => setMprOpen(!mprOpen)}>
            <Icon name={mprOpen ? 'chevron-right' : 'chevron-left'} />
          </button>
          {mprOpen && state && <MprPanel session={session} state={state} onFullscreen={toggleFullscreen} onQuad={() => setLayout({ mode: 'quad' })} />}
        </div>
      )}

      {guide && <ControlsGuideDialog onClose={() => setGuide(false)} />}
      {settings && <ViewSettingsDialog speeds={speeds} onChange={setSpeeds} onReset={() => setSpeeds(defaultSpeeds())} onClose={() => setSettings(false)} />}
    </div>
  );
}
